"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Search, ShoppingCart, User, Menu, X, Heart } from "lucide-react";
import base from "lib/base";
import { languageBuild } from "lib/language";
import { useWebInfoStore } from "context/webInfoStore";
import { useCartStore } from "context/cartStore";
import { useWishStore } from "context/wishStore";
import { useAuthUser } from "hooks/useAuth";
import CartDrawer from "./CartDrawer";
import WishDrawer from "./WishDrawer";
import styles from "styles/components/header.module.css";
import { Badge } from "antd";

const Header = ({ language = "mn" }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const mobileCloseBtnRef = useRef(null);
  const webInfo = useWebInfoStore((state) => state.webInfo);
  const menuItems = useWebInfoStore((state) => state.menus);
  const { items, toggleCart } = useCartStore();
  const wishItems = useWishStore((state) => state.items);
  const toggleWishDrawer = useWishStore((state) => state.toggleWishDrawer);
  const pathname = usePathname();
  const router = useRouter();
  const lang = language || "mn";
  const user = useAuthUser();

  const cartCount = items.length;
  const wishCount = wishItems.length;

  const logoPath = languageBuild(webInfo, "logo", lang) || webInfo?.logo;
  const siteSubtitle = languageBuild(webInfo, "siteShortInfo", lang) || "";
  const siteTitle =
    languageBuild(webInfo, "siteTitle", lang) ||
    webInfo?.siteTitle ||
    "Gerelt";
  const logoUrl = logoPath ? `${base.cdnUrl}/${logoPath}` : null;

  const computedMenus = useMemo(() => {
    if (!Array.isArray(menuItems)) return [];
    return menuItems;
  }, [menuItems]);

  const profileHref = user
    ? "/profile"
    : `/auth/login?redirect=${encodeURIComponent(pathname || "/profile")}`;

  const isNavActive = (href) => {
    if (!href || href === "#") return false;
    const pathOnly = (pathname || "").split("?")[0];
    if (href === "/") return pathOnly === "/";
    return pathOnly === href || pathOnly.startsWith(`${href}/`);
  };

  const getMenuHref = (menu) => {
    const {
      linkType,
      url,
      internalPath,
      path,
      slug,
      direct,
      model,
      newsCategory,
      isDirect,
      isModel,
      isCategory,
    } = menu || {};

    if (linkType === "externalUrl") return url || "#";
    if (linkType === "internalPath") return path || internalPath || "#";
    if (linkType === "internalPage" || linkType === "pageList") {
      return `/page/${slug || ""}`;
    }
    if (linkType === "subMenuList") {
      return `/page/${slug || ""}`;
    }

    if (isDirect) return direct || "#";
    if (isModel) return model ? `/${model}` : "#";
    if (isCategory)
      return newsCategory ? `/news?categories=${newsCategory}` : "#";

    if (direct) return direct;
    if (model) return `/${model}`;
    if (slug) return `/page/${slug}`;
    return path || internalPath || "#";
  };

  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const toggleMobileMenu = () => setIsMobileMenuOpen((prev) => !prev);

  useEffect(() => {
    const onScroll = () => setHeaderScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeMobileMenu();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      requestAnimationFrame(() => mobileCloseBtnRef.current?.focus());
    }
  }, [isMobileMenuOpen]);

  useEffect(() => {
    closeMobileMenu();
  }, [pathname]);

  const handleSearch = (e) => {
    e.preventDefault();
    const q = searchText.trim();
    if (q) {
      router.push(`/books?q=${encodeURIComponent(q)}&type=all`);
    }
  };

  const searchForm = (
    <form
      className={styles.search}
      onSubmit={handleSearch}
      role="search"
    >
      <button
        type="submit"
        className={styles.searchSubmit}
        aria-label="Хайх"
      >
        <Search size={22} strokeWidth={2} aria-hidden />
      </button>
      <input
        type="search"
        name="q"
        placeholder="Зохиолч, номны хайх..."
        className={styles.searchInput}
        aria-label="Ном хайх"
        autoComplete="off"
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
      />
    </form>
  );

  const MobileMenu = () => (
    <div
      id="mobile-drawer-nav"
      className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ""}`}
      role="dialog"
      aria-modal="true"
      aria-label="Гол цэс"
      aria-hidden={!isMobileMenuOpen}
    >
      <div className={styles.mobileMenuHeader}>
        <span className={styles.mobileMenuTitle}>Цэс</span>
        <button
          ref={mobileCloseBtnRef}
          type="button"
          className={styles.closeBtn}
          aria-label="Цэс хаах"
          onClick={closeMobileMenu}
        >
          <X size={20} strokeWidth={2} aria-hidden />
        </button>
      </div>
      <nav className={styles.mobileNav} aria-label="Гол навигаци">
        {computedMenus.map((menu) => {
          const href = getMenuHref(menu);
          const label = languageBuild(menu, "name", lang) || menu?.name || "";
          if (!label) return null;
          const isExternal = menu?.linkType === "externalUrl";

          if (isExternal) {
            return (
              <a
                key={menu?._id || href || label}
                href={href}
                className={styles.mobileNavLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                {label}
              </a>
            );
          }

          return (
            <Link
              key={menu?._id || href || label}
              href={href}
              className={`${styles.mobileNavLink} ${isNavActive(href) ? styles.mobileNavLinkActive : ""}`}
              onClick={closeMobileMenu}
            >
              {label}
            </Link>
          );
        })}
      </nav>
    </div>
  );

  const brandAriaLabel = `${siteTitle} — Нүүр хуудас`;

  return (
    <header
      className={`${styles.header} ${headerScrolled ? styles.headerScrolled : ""}`}
    >
      <div className={`container ${styles.headerContainer}`}>
        <div className={styles.wrapper}>
          <div className={styles.leftGroup}>
            <div className={styles.logoburger}>
              <Link href="/" className={styles.brand} aria-label={brandAriaLabel}>
                {logoUrl ? (
                  <img
                    src={logoUrl}
                    alt=""
                    className={styles.logo}
                    decoding="async"
                  />
                ) : (
                  <span className={styles.logoFallback}>{siteTitle}</span>
                )}
                {siteSubtitle ? (
                  <span className={styles.brandText}>
                    <span className={styles.brandSubtitle}>{siteSubtitle}</span>
                  </span>
                ) : null}
              </Link>
              <div className={styles.toolbarMobile}>
                <Link
                  href="/search"
                  className={styles.toolbarBtn}
                  aria-label="Хайх хуудас"
                  prefetch={false}
                >
                  <Search size={20} strokeWidth={2} aria-hidden />
                </Link>
                <button
                  type="button"
                  className={styles.toolbarBtn}
                  aria-label="Сагс"
                  onClick={toggleCart}
                >
                  <Badge count={cartCount} size="small" color="#fc9f00">
                    <ShoppingCart size={20} strokeWidth={2} aria-hidden />
                  </Badge>
                </button>
                <button
                  type="button"
                  className={styles.toolbarBtn}
                  onClick={toggleMobileMenu}
                  aria-label={isMobileMenuOpen ? "Цэс хаах" : "Цэс нээх"}
                  aria-expanded={isMobileMenuOpen}
                  aria-controls="mobile-drawer-nav"
                >
                  {isMobileMenuOpen ? (
                    <X size={22} strokeWidth={2} aria-hidden />
                  ) : (
                    <Menu size={22} strokeWidth={2} aria-hidden />
                  )}
                </button>
              </div>
            </div>

            <nav className={styles.nav} aria-label="Гол цэс">
              {computedMenus.map((menu) => {
                const href = getMenuHref(menu);
                const label =
                  languageBuild(menu, "name", lang) || menu?.name || "";
                if (!label) return null;

                const isExternal = menu?.linkType === "externalUrl";
                const active = isNavActive(href);

                if (isExternal) {
                  return (
                    <a
                      key={menu?._id || href || label}
                      href={href}
                      className={`${styles.navLink} ${active ? styles.navLinkActive : ""}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {label}
                    </a>
                  );
                }

                return (
                  <Link
                    key={menu?._id || href || label}
                    href={href}
                    className={`${styles.navLink} ${active ? styles.navLinkActive : ""}`}
                  >
                    {label}
                  </Link>
                );
              })}
            </nav>
          </div>

          <div className={styles.rightGroup}>
            {searchForm}
            <div className={styles.desktopIcons}>
              <button
                type="button"
                className={styles.iconButton}
                aria-label="Сагс"
                onClick={toggleCart}
              >
                <Badge
                  count={cartCount}
                  size="small"
                  color="#fc9f00"
                  offset={[-2, 2]}
                >
                  <ShoppingCart size={20} strokeWidth={2} aria-hidden />
                </Badge>
              </button>
              <button
                type="button"
                className={styles.iconButton}
                aria-label="Хүслийн жагсаалт"
                onClick={toggleWishDrawer}
              >
                <Badge
                  count={wishCount}
                  size="small"
                  color="#e53935"
                  offset={[-2, 2]}
                >
                  <Heart size={20} strokeWidth={2} aria-hidden />
                </Badge>
              </button>
              <Link
                href={profileHref}
                className={styles.iconButton}
                aria-label={user ? "Профайл" : "Нэвтрэх"}
                prefetch={false}
              >
                {user ? (
                  user.avatar ? (
                    <img
                      src={
                        user.avatar.startsWith("http")
                          ? user.avatar
                          : `${base.cdnUrl}/${user.avatar}`
                      }
                      alt=""
                      className={styles.userAvatar}
                    />
                  ) : (
                    <div className={styles.userInitial} aria-hidden>
                      {(user.name || user.email || "U").charAt(0).toUpperCase()}
                    </div>
                  )
                ) : (
                  <User size={20} strokeWidth={2} aria-hidden />
                )}
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        type="button"
        className={`${styles.overlay} ${isMobileMenuOpen ? styles.overlayOpen : ""}`}
        aria-label="Цэс хаах"
        tabIndex={-1}
        onClick={closeMobileMenu}
      />

      <MobileMenu />

      <CartDrawer />
      <WishDrawer />
    </header>
  );
};

export default Header;
